import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { Archive, ArchiveRestore, Check, FolderKanban, Pencil, Plus, X } from "lucide-react";
import {
  archiveProject,
  createProject,
  swrFetcher,
  unarchiveProject,
  updateProject,
  type ProjectInfo,
} from "@/lib/api";
import { useProjectStore } from "@/store/project";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog } from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/ui/empty-state";

export function ProjectsPage() {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState(false);

  const { data: projects, isLoading, mutate } = useSWR<ProjectInfo[]>(
    `/api/projects?include_archived=${showArchived}`,
    swrFetcher
  );

  const selectedProjectId = useProjectStore((s) => s.selectedProjectId);
  const setSelectedProject = useProjectStore((s) => s.setSelectedProject);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Inline rename
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const active = (projects ?? []).filter((p) => !p.archived);
  const archived = (projects ?? []).filter((p) => p.archived);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setCreateError(null);
    try {
      const project = await createProject(name, newDescription.trim() || undefined);
      setCreateOpen(false);
      setNewName("");
      setNewDescription("");
      setSelectedProject(project.id);
      mutate();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const startRename = (p: ProjectInfo) => {
    setEditingId(p.id);
    setDraftName(p.name);
  };

  const saveRename = async (p: ProjectInfo) => {
    const name = draftName.trim();
    if (!name || name === p.name) {
      setEditingId(null);
      return;
    }
    setBusyId(p.id);
    try {
      await updateProject(p.id, { name });
      setEditingId(null);
      mutate();
    } finally {
      setBusyId(null);
    }
  };

  const toggleArchive = async (p: ProjectInfo) => {
    setBusyId(p.id);
    try {
      if (p.archived) {
        await unarchiveProject(p.id);
      } else {
        await archiveProject(p.id);
        // Don't keep an archived project as the active scope.
        if (selectedProjectId === p.id) setSelectedProject(null);
      }
      mutate();
    } finally {
      setBusyId(null);
    }
  };

  const openProject = (p: ProjectInfo) => {
    setSelectedProject(p.id);
    navigate("/");
  };

  const renderCard = (p: ProjectInfo) => {
    const isSelected = p.id === selectedProjectId;
    const isEditing = editingId === p.id;
    const busy = busyId === p.id;

    return (
      <Card key={p.id} className={cn("p-4", p.archived && "opacity-60")}>
        <div className="flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-start">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <FolderKanban
                className={cn("h-4 w-4 shrink-0", p.archived ? "text-studio-faint" : "text-studio-accent")}
              />
              {isEditing ? (
                <Input
                  autoFocus
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveRename(p);
                    if (e.key === "Escape") setEditingId(null);
                  }}
                  className="h-8 flex-1"
                />
              ) : (
                <h3 className="truncate font-display font-semibold text-studio-ink">{p.name}</h3>
              )}
              {isSelected && !p.archived && <Badge variant="accent">Selected</Badge>}
              {p.archived && <Badge variant="neutral">Archived</Badge>}
            </div>
            {p.description ? (
              <p className="mt-1.5 line-clamp-2 text-xs text-studio-muted">{p.description}</p>
            ) : (
              <p className="mt-1.5 text-xs text-studio-faint">No description</p>
            )}
            <p className="mt-1 font-mono text-[10px] text-studio-faint">/{p.slug}</p>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-1">
            {isEditing ? (
              <>
                <Button size="icon" variant="ghost" loading={busy} onClick={() => saveRename(p)} title="Save">
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setEditingId(null)} title="Cancel">
                  <X className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                {!p.archived && (
                  <Button size="icon" variant="ghost" onClick={() => startRename(p)} title="Rename">
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  loading={busy}
                  onClick={() => toggleArchive(p)}
                  title={p.archived ? "Unarchive" : "Archive"}
                >
                  {p.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                </Button>
              </>
            )}
          </div>
        </div>

        {!p.archived && !isEditing && (
          <div className="mt-3 border-t border-studio-border pt-3">
            <Button
              className="w-full sm:w-auto"
              size="sm"
              variant={isSelected ? "secondary" : "primary"}
              onClick={() => openProject(p)}
              disabled={busy}
            >
              {isSelected ? "Open in Studio" : "Open"}
            </Button>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="page-container max-w-5xl">
      <div className="page-header sm:items-center">
        <div className="flex items-center gap-3">
          <FolderKanban className="h-5 w-5 text-studio-accent" />
          <h1 className="font-display text-xl font-bold tracking-tight">Projects</h1>
        </div>
        <Button className="w-full sm:w-auto" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          New project
        </Button>
      </div>

      {isLoading && !projects ? (
        <p className="text-sm text-studio-muted">Loading projects…</p>
      ) : (
        <div className="space-y-8">
          <section>
            <div className="mb-3 flex flex-col items-stretch justify-between gap-2 sm:flex-row sm:items-center">
              <h2 className="text-[11px] font-semibold uppercase tracking-wider text-studio-muted">
                Active ({active.length})
              </h2>
              <label className="flex min-h-11 cursor-pointer items-center justify-between gap-2 rounded-lg text-xs text-studio-muted sm:justify-start">
                Show archived
                <Switch checked={showArchived} onCheckedChange={setShowArchived} />
              </label>
            </div>
            {active.length === 0 ? (
              <EmptyState
                icon={FolderKanban}
                size="md"
                title="No projects yet"
                hint="Create a project to organize your generations"
              />
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">{active.map(renderCard)}</div>
            )}
          </section>

          {showArchived && archived.length > 0 && (
            <section>
              <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-studio-muted">
                Archived ({archived.length})
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">{archived.map(renderCard)}</div>
            </section>
          )}
        </div>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} title="New project">
        <div className="space-y-4 p-4 sm:p-5">
          <Field label="Name" required>
            <Input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Summer Campaign"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newName.trim()) handleCreate();
              }}
            />
          </Field>
          <Field label="Description" hint={<span className="ml-1 text-studio-faint normal-case">optional</span>}>
            <Textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="What is this project about?"
              rows={3}
            />
          </Field>
          {createError && (
            <p className="rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">{createError}</p>
          )}
          <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={creating} disabled={!newName.trim()}>
              Create
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
